<template>
    <div class="kallelser-och-sms">
        <div class="header">
            <h1>Kallelser och sms</h1>
            <button type="button" @click="openSmsMallarTab">
                <img src="../../assets/icons/gear_icon.svg" />
                Hantera sms-mallar
            </button>
            <button type="button" @click="openKallelsemallarTab">
                <img src="../../assets/icons/gear_icon.svg" />
                Hantera kallelsemallar
            </button>
        </div>

        <search-filter v-model="filterValues" @input="handleSearch" :fields="filterFields">
            <template v-slot:header-filters>
                <FormulateInput class="search-field" name="q" placeholder="Sök patient" />
            </template>
            <template v-slot:fields>
                <FormulateInput
                    name="huvudoperationskod"
                    label="Huvudoperationskod"
                    type="vueselect"
                    :options="huvudoperationskoder"
                    @search="searchHuvudoperationskoder"
                    class="autocomplete"
                />
                <FormulateInput
                    type="datepicker"
                    name="bokad_operation_datum__gte"
                    label="Bokad operationsdatum fr.o.m."
                />
                <FormulateInput
                    type="datepicker"
                    name="bokad_operation_datum__lte"
                    label="Bokad operationsdatum t.o.m."
                />

                <FormulateInput type="vueselect" name="bokad_sal" label="Operationssal" :options="operationssalar" />

                <FormulateInput
                    name="huvudoperator"
                    label="Huvudoperatör"
                    type="vueselect"
                    :options="huvudoperatorer"
                    @search="searchHuvudoperatorer"
                    class="autocomplete"
                />

                <FormulateInput
                    type="vueselect"
                    name="sms_status"
                    label="Status sms"
                    :options="[
                        { value: 'Sms besvarat', label: 'Sms besvarat' },
                        { value: 'Sms skickat', label: 'Sms skickat' },
                        { value: 'Ej skickat', label: 'Ej skickat' },
                        { value: 'Ej sms', label: 'Ej sms' },
                    ]"
                />

                <FormulateInput type="vueselect" name="smsmall" label="Sms-mall" :options="smsmallar" />

                <FormulateInput
                    type="vueselect"
                    name="kallelse_status"
                    label="Status kallelse"
                    :options="[
                        { value: 'Ej skapad', label: 'Ej skapad' },
                        { value: 'Kallelse skapad', label: 'Kallelse skapad' },
                        { value: 'Kallelse utskriven', label: 'Kallelse utskriven' },
                        { value: 'Kallelse skickad', label: 'Kallelse skickad' },
                    ]"
                />

                <FormulateInput type="vueselect" name="kallelsemall" label="Kallelsemall" :options="kallelsemallar" />
            </template>
        </search-filter>
        <kliniken-table
            ref="klinikenTable"
            :items="items"
            :columns="columns"
            @sort="sort"
            :dropdown-actions="dropdownActions"
            :has-dropdown="hasDropdown"
            :show-checkboxes="true"
            :hideCheckboxProperty="'sms_status'"
            :hideCheckboxValue="'Ej sms'"
            :showBottom="false"
            @sendSms="sendSms"
            @sendKallelse="sendKallelse"
            :selectedItem="selectedItem"
            @tableRowClicked="tableRowClicked"
            @handleCheckedIds="handleCheckedIds"
        >
            <template v-slot:custom-bottom>
                <div class="send-sms-container">
                    <span> {{ selectedCheckboxes.length }} objekt valda</span>
                    <ButtonGroup>
                        <Button size="secondary" @click="sendMultipleSms" :disabled="selectedCheckboxes.length === 0"
                            >Skapa sms ({{ selectedCheckboxes.length }})
                        </Button>
                        <Button type="button" @click="cancel" variant="contour" size="secondary">Avbryt</Button>
                    </ButtonGroup>
                </div>
            </template>
        </kliniken-table>
    </div>
</template>

<script>
    import { mapGetters } from "vuex";
    import BaseTab from "@/tabs/mixins/BaseTab.js";
    import tabIcon from "@/assets/actionIcons/send.svg";
    import KlinikenTable from "@/components/Table/KlinikenTable.vue";
    import { getConfig, klinikenApi } from "@/api";
    import SearchFilter from "@/components/SearchFilter.vue";
    import ButtonGroup from "@/components/ButtonGroup.vue";
    import Button from "@/components/Button.vue";
    import SMSSend from "@/tabs/Operation/SMS/SMSSend.vue";
    import KallelseSend from "@/tabs/Operation/Kallelser/KallelseSend.vue";
    import tabEvents from "@/tabEvents";
    import StatusSMSBesvaratIcon from "@/assets/actionIcons/status_sms_besvarat.svg";
    import StatusSMSSkickatIcon from "@/assets/actionIcons/status_sms_skickat.svg";
    import StatusSMSEjSkickatIcon from "@/assets/actionIcons/status_sms_ej_skickat.svg";
    import StatusSMSEjIcon from "@/assets/actionIcons/status_sms_ej.svg";
    import IconSMS from "@/assets/actionIcons/sms.svg";
    import StatusKallelseEjSkapadIcon from "@/assets/actionIcons/status_kallelse_ej_skapad.svg";
    import StatusKallelseSkapadIcon from "@/assets/actionIcons/status_kallelse_skapad.svg";
    import StatusKallelseUtskrivenIcon from "@/assets/actionIcons/status_kallelse_utskriven.svg";
    import StatusKallelseSkickadIcon from "@/assets/actionIcons/status_kallelse_skickad.svg";
    import SkapaKallelseIcon from "@/assets/actionIcons/skapa_kallelse.svg";
    import SMSMallar from "@/tabs/Operation/SMS/SMSMallar.vue";
    import Kallelsemallar from "@/tabs/Operation/Kallelser/Kallelsemallar.vue";
    import emitter from "tiny-emitter/instance";
    import { flashWarning } from "@/utils";
    import { profileOptions } from "@/tabs/Operation/utils";

    export default {
        extends: BaseTab,
        name: "SMSKallelser",
        tabName: "Kallelser och sms",
        icon: tabIcon,
        refreshTab: true,
        components: {
            KlinikenTable,
            SearchFilter,
            ButtonGroup,
            Button,
        },
        props: {
            tabId: Number,
        },
        data() {
            return {
                count: 0,
                filterValues: {},
                sortField: "bokad_operation_datum",
                huvudoperationskoder: [],
                huvudoperatorer: [],
                operationssalar: [],
                items: [],
                columns: [
                    {
                        title: "PersonID",
                        name: "patient_display.personId_formatted",
                        sort: "patient__personId",
                    },
                    {
                        title: "Patientnamn",
                        name: "patient_display.namn_reverse",
                        sort: "patient__namn",
                    },
                    {
                        title: "Huvudopkod",
                        name: "huvudoperationskod_display.code",
                        sort: "huvudoperationskod",
                    },
                    {
                        title: "Bokat opdatum",
                        name: "bokad_operation_datum",
                        sort: "bokad_operation_datum",
                    },
                    {
                        title: "Optid",
                        name: "bokad_operation_tid",
                        sort: "bokad_operation_tid",
                        type: "time",
                    },
                    {
                        title: "Inställelsetid",
                        name: "installelsetid",
                        sort: "installelsetid",
                        type: "time",
                    },
                    {
                        title: "Kallelsemall",
                        name: "kallelsemallnamn",
                        sort: "",
                        type: "mall",
                    },
                    {
                        title: "Status kallelse",
                        name: "kallelse_status",
                        sort: "",
                        type: "icon",
                        getContent: function (item) {
                            if (item.kallelse_status === "Kallelse utskriven" && item.kallelse_utskriven_datum) {
                                // remove time from kallelse_utskriven_datum and return it with the status
                                return `${item.kallelse_status} ${item.kallelse_utskriven_datum.split("T")[0]}`;
                            }
                            if (item.kallelse_status === "Kallelse skickad" && item.kallelse_skickad_datum) {
                                return `${item.kallelse_status} ${item.kallelse_skickad_datum.split("T")[0]}`;
                            }
                            return item.kallelse_status;
                        },
                        iconName: "kallelse_status",
                        icons: {
                            "Ej skapad": StatusKallelseEjSkapadIcon,
                            "Kallelse skapad": StatusKallelseSkapadIcon,
                            "Kallelse utskriven": StatusKallelseUtskrivenIcon,
                            "Kallelse skickad": StatusKallelseSkickadIcon,
                        },
                    },
                    {
                        title: "Sms-mall",
                        name: "smsmall.namn",
                        sort: "",
                        type: "mall",
                    },
                    {
                        title: "Status sms",
                        name: "sms_status",
                        type: "icon",
                        icons: {
                            "Ej sms": StatusSMSEjIcon,
                            "Sms skickat": StatusSMSSkickatIcon,
                            "Ej skickat": StatusSMSEjSkickatIcon,
                            "Sms besvarat": StatusSMSBesvaratIcon,
                        },
                    },
                ],
                dropdownActions: [
                    {
                        title: "Skapa sms",
                        name: "sendSms",
                        show: ({ patient_display }) => patient_display?.onskar_sms === true,
                        icon: IconSMS,
                    },

                    {
                        title: "Skapa kallelse",
                        name: "sendKallelse",
                        show: () => true,
                        icon: SkapaKallelseIcon,
                    },
                ],
                hasDropdown: true,
                selectedCheckboxes: [],
                selectedItem: null,
                kallelsemallar: [],
                smsmallar: [],
            };
        },

        watch: {
            currentId() {
                if (this.currentId !== this.selectedItem?.id) {
                    this.selectedItem = null;
                }
            },
        },

        computed: {
            ...mapGetters("patientData", ["currentId"]),
            ...mapGetters("userData", ["currentProfile"]),
            ...mapGetters("ability", ["can"]),
            filterFields() {
                return {
                    q: { label: "Sök " },
                    huvudoperationskod: { label: "Huvudoperationskod ", options: this.huvudoperationskoder },
                    bokad_operation_datum__gte: { label: "Bokad operationsdatum fr.o.m. " },
                    bokad_operation_datum__lte: { label: "Bokad operationsdatum t.o.m. " },
                    huvudoperator: { label: "Huvudoperatör", options: this.huvudoperatorer },
                    bokad_sal: { label: "Bokad sal", options: this.operationssalar },
                    sms_status: { label: "Status sms " },
                    smsmall: { label: "Sms-mall ", options: this.smsmallar },
                    kallelse_status: {
                        label: "Status kallelse ",
                        options: [
                            { value: "Kallelse skapad", label: "Kallelse skapad" },
                            { value: "Kallelse utskriven", label: "Kallelse utskriven" },
                            { value: "Kallelse skickad", label: "Kallelse skickad" },
                        ],
                    },
                    kallelsemall: { label: "Kallelsemall ", options: this.kallelsemallar },
                };
            },
        },

        methods: {
            async handleSearch(filterData) {
                this.filterValues = filterData;
                await this.fetchAnmalningarKallelserSms();
            },

            async sort(field) {
                this.sortField = field;
                await this.fetchAnmalningarKallelserSms();
            },

            async tableRowClicked(item) {
                if (item) {
                    this.selectedItem = item;
                    const id = item?.patient_display?.id;
                    await this.$store.dispatch("patientData/loadFromBackend", {
                        id,
                        failSilently: false,
                    });
                }
            },

            async searchHuvudoperationskoder(search) {
                if (search.length >= 3) {
                    const response = await klinikenApi.get(
                        "/operation/kodverk/operationskoder/all/",
                        getConfig({ params: { q: search } })
                    );
                    const { data: huvudoperationskoder } = response;

                    this.huvudoperationskoder = huvudoperationskoder.map(({ id, code, displayName }) => ({
                        value: id,
                        label: `${code} ${displayName}`,
                    }));
                }
            },

            async searchHuvudoperatorer(search) {
                const currentOrgUnitId = this.currentProfile.orgUnit.id;

                if (search.length >= 3) {
                    try {
                        const url = `/core/profiles/`;
                        const operatorerResponse = await klinikenApi.get(
                            url,
                            getConfig({
                                params: { q: search, is_active: true, orgUnit: currentOrgUnitId, operator: true },
                            })
                        );

                        const { results: huvudoperatorer } = operatorerResponse.data;

                        this.huvudoperatorer = profileOptions(huvudoperatorer, true);
                    } catch (err) {
                        flashWarning("Kunde inte söka efter huvudoperatörer");
                    }
                }
            },

            sendSms(receiver) {
                this.openSmsSend([receiver]);
            },

            sendKallelse(receiver) {
                this.openKallelseSend(receiver);
            },

            sendMultipleSms() {
                this.openSmsSend(this.selectedCheckboxes);
            },

            openSmsMallarTab() {
                this.$store.dispatch("tabs/openTab", {
                    component: SMSMallar,
                });
            },

            openKallelsemallarTab() {
                this.$store.dispatch("tabs/openTab", {
                    component: Kallelsemallar,
                });
            },

            async cancel() {
                await this.$store.dispatch("tabs/closeTab", this.tabId);
            },

            /**
             * Handles checked IDs.
             *
             * @param {Array.<string>} checkedIds - An array of string IDs.
             * @returns {void}
             */
            handleCheckedIds(checkedIds = []) {
                const foundItems = this.findItemsByIds(checkedIds, this.items);
                this.selectedCheckboxes = foundItems;
            },

            /**
             * Hämtar operationsanmälningar som är bokade och i framtiden
             *
             * @returns {Promise<void>} A promise that resolves when the operations are fetched successfully.
             * @throws {Error} If there is an error while fetching the operations.
             */
            async fetchAnmalningarKallelserSms() {
                this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: true });

                let params = {
                    ...this.filterValues,
                    ...{
                        ordering: this.sortField,
                    },
                };
                try {
                    const response = await klinikenApi.get("operation/kallelsersms/", getConfig({ params: params }));
                    const { results: items, count } = response.data;
                    this.items = items;
                    this.count = count;
                } catch (_) {
                    flashWarning("Kunde inte hämta anmälningar");
                } finally {
                    this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });
                }
            },

            async fetchKallelsemallar() {
                try {
                    const response = await klinikenApi.get("kallelser/mallar/", getConfig());
                    const { results: kallelsemallar } = response.data;

                    kallelsemallar.forEach((mall) => {
                        this.kallelsemallar.push({ value: mall.id, label: mall.namn });
                    });
                } catch (_) {
                    flashWarning("Kunde inte hämta kallelsemallar");
                }
            },

            async fetchSmsMallar() {
                try {
                    const response = await klinikenApi.get("/sms/mallar/", getConfig());
                    const { results: smsmallar } = response.data;

                    smsmallar.forEach((mall) => {
                        this.smsmallar.push({ value: mall.id, label: mall.namn });
                    });
                } catch (_) {
                    flashWarning("Kunde inte hämta sms-mallar");
                }
            },

            async fetchOperationssalar() {
                try {
                    const response = await klinikenApi.get("/operation/salar", getConfig());
                    const { results: operationssalar } = response.data;

                    this.operationssalar = operationssalar.map((sal) => {
                        return {
                            value: sal.id,
                            label: sal.namn,
                        };
                    });
                } catch (_) {
                    flashWarning("Kunde inte hämta operationssalar");
                }
            },

            refreshSmsKallelser() {
                this.$refs.klinikenTable.uncheckAll();
                this.selectedCheckboxes = [];
                this.fetchAnmalningarKallelserSms();
            },

            /**
             * Opens an Sms send tab.
             *
             * @param {Array} receivers - Containing a list of SMS receivers.
             * @param {string} [baseUrl="/operation/anmalan/"] - The base URL for the operation.
             * @param {string} [path="/sms/"] - The path for the SMS operation.
             */
            openSmsSend(receivers, baseUrl = "/operation/anmalan/", path = "/sms/") {
                this.$store.dispatch("tabs/openTab", {
                    component: SMSSend,
                    parameters: { receivers, baseUrl, path },
                });
            },

            openKallelseSend(receiver, baseUrl = "/operation/anmalan/", path = "/kallelse/") {
                this.$store.dispatch("tabs/openTab", {
                    component: KallelseSend,
                    parameters: { receiver, baseUrl, path },
                });
            },

            /**
             * Filter items based on a list of IDs.
             * @param {string[]} list - The list of IDs to filter.
             * @param {Array} items - The array of items to filter.
             * @returns {Array} - The filtered items.
             */
            findItemsByIds(list = [], items = []) {
                return items.filter((item) => list.includes(item.id)) ?? [];
            },
        },
        async created() {
            if (!this.can({ action: "kallelse", subject: "anmalan" })) {
                await this.cancel();

                return;
            }

            this.$on("sendSms", (item) => {
                this.sendSms(item);
            });

            await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: true });
            tabEvents.$on("refreshSmsKallelser", this.refreshSmsKallelser);
            emitter.on("kallelse", this.fetchAnmalningarKallelserSms);
            await this.fetchAnmalningarKallelserSms();
            await this.fetchKallelsemallar();
            await this.fetchSmsMallar();
            await this.fetchOperationssalar();
            await this.$store.dispatch("tabs/displaySpinner", { id: this.tabId, display: false });
        },
        mounted() {
            emitter.on(`refreshTab-${this.tabId}`, async () => {
                await this.fetchAnmalningarKallelserSms();
            });
        },

        beforeDestroy() {
            tabEvents.$off("refreshSmsKallelser");
            emitter.off(`refreshTab-${this.tabId}`);
        },
    };
</script>

<style lang="scss" scoped>
    .kallelser-och-sms {
        padding: 30px 0px;

        .header {
            display: flex;
            align-items: center;
            gap: 40px;
            margin-bottom: 26px;
            margin-top: -30px;

            h1 {
                margin: 0 !important;
            }

            .button-hollow:hover {
                background-color: transparent;
            }

            button {
                background: transparent;
                border: none;
                display: flex;
                align-items: center;
                justify-content: center;

                img {
                    margin-right: 8px;
                }
            }
        }

        .search-field {
            width: calc(256px * 1.5);
        }

        .send-sms-container {
            background-color: #f3f5f9;
            min-height: 75px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            row-gap: 20px;
            padding: 10px;
            margin: 15px 0px;

            span {
                margin: 0 16px;
            }
        }
    }
</style>
